import { z } from 'zod';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Who may call an endpoint. The server enforces it; clients use it to decide
 * whether credentials (cookie + CSRF header, or bearer token) are attached.
 * `admin` = company owner or admin. `owner` = company owner only. Both also
 * need a session that passed two-step verification at sign-in.
 */
export type AuthRequirement = 'public' | 'session' | 'admin' | 'owner';

/** Rate-limit bucket applied by the server. */
export type RateLimitBucket = 'auth' | 'refresh' | 'invite' | 'upload' | 'avatar' | 'profile' | 'search' | 'default';

/** Sentinel for "no params / query / body". */
export const NoInput = z.undefined();
export type NoInput = typeof NoInput;

/** Marker for endpoints that stream bytes instead of JSON (downloads, previews). */
export const BINARY = 'binary' as const;
export type Binary = typeof BINARY;

export interface Endpoint<
  P extends z.ZodType = NoInput,
  Q extends z.ZodType = NoInput,
  B extends z.ZodType = NoInput,
  R extends z.ZodType | Binary = z.ZodType,
> {
  /** Stable identifier, e.g. `messages.send`. */
  readonly id: string;
  readonly method: HttpMethod;
  /** Express-style path with `:name` params, always under `/api/v1` or `/.well-known`. */
  readonly path: `/${string}`;
  readonly auth: AuthRequirement;
  readonly params: P;
  readonly query: Q;
  readonly body: B;
  /** JSON response schema, or `binary` for streamed content. 204 responses use `NoContent`. */
  readonly response: R;
  readonly successStatus: 200 | 201 | 202 | 204 | 206;
  readonly rateLimit: RateLimitBucket;
  /** Body is raw bytes (`application/octet-stream`), e.g. upload content. */
  readonly rawBody: boolean;
}

/** 204 No Content. */
export const NoContent = z.undefined();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyEndpoint = Endpoint<any, any, any, any>;

export function endpoint<
  P extends z.ZodType = NoInput,
  Q extends z.ZodType = NoInput,
  B extends z.ZodType = NoInput,
  R extends z.ZodType | Binary = z.ZodType,
>(def: {
  id: string;
  method: HttpMethod;
  path: `/${string}`;
  auth: AuthRequirement;
  params?: P;
  query?: Q;
  body?: B;
  response: R;
  successStatus?: Endpoint['successStatus'];
  rateLimit?: RateLimitBucket;
  rawBody?: boolean;
}): Endpoint<P, Q, B, R> {
  return {
    id: def.id,
    method: def.method,
    path: def.path,
    auth: def.auth,
    params: (def.params ?? NoInput) as P,
    query: (def.query ?? NoInput) as Q,
    body: (def.body ?? NoInput) as B,
    response: def.response,
    successStatus: def.successStatus ?? ((def.response as unknown) === NoContent ? 204 : 200),
    rateLimit: def.rateLimit ?? 'default',
    rawBody: def.rawBody ?? false,
  };
}

type Absent<S> = S extends z.ZodUndefined ? true : false;

/** Input accepted by a typed client call for endpoint `E`. */
export type EndpointInput<E extends AnyEndpoint> = (Absent<E['params']> extends true
  ? { params?: undefined }
  : { params: z.input<E['params']> }) &
  (Absent<E['query']> extends true ? { query?: undefined } : { query?: z.input<E['query']> }) &
  (Absent<E['body']> extends true ? { body?: undefined } : { body: z.input<E['body']> });

/** Parsed success value of endpoint `E`. */
export type EndpointOutput<E extends AnyEndpoint> = E['response'] extends z.ZodType ? z.output<E['response']> : Response;

/** Fills `:name` segments. Throws when a param is missing so bad calls fail loudly. */
export function buildPath(path: string, params: Record<string, string | number> | undefined): string {
  return path.replace(/:([A-Za-z][A-Za-z0-9]*)/g, (_, name: string) => {
    const value = params?.[name];
    if (value === undefined || value === '') throw new Error(`missing path param "${name}" for ${path}`);
    return encodeURIComponent(String(value));
  });
}

/** Serialises a flat query object; undefined/null values are omitted. */
export function buildQuery(query: Record<string, unknown> | undefined): string {
  if (!query) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) value.forEach((item) => search.append(key, String(item)));
    else search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}
